import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Avatar,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Edit,
    ArrowBack,
    Business,
    Email,
    Phone,
    Public,
    Work
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Consultant } from './ConsultantList';

const ConsultantDetails: React.FC = () => {
    const { user, isTenantAdmin, isGlobalAdmin } = useUser();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [consultant, setConsultant] = useState<Consultant | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useDocumentTitle(consultant ? `${consultant.firstName} ${consultant.lastName}` : 'Consultant Details');

    useEffect(() => {
        if (id) {
            loadConsultant();
        }
    }, [id]);

    const loadConsultant = async () => {
        // TODO: Load consultant data from Firestore
        setLoading(false);
        if (!id) return;

        // Placeholder data
        setConsultant({
            id,
            firstName: 'John',
            lastName: 'Doe',
            email: 'john.doe@example.com',
            phone: '+1234567890',
            company: 'Tech Corp',
            jobTitle: 'Senior Consultant',
            country: 'United States',
            isRegisteredUser: true,
            isActive: true,
            createdAt: { seconds: Date.now() / 1000 },
            // presentations: [] // Assuming this might be added later
        } as any);
    };

    const canEditConsultant = () => {
        if (!consultant || !user) return false;
        return isGlobalAdmin() || (isTenantAdmin() && consultant.tenantId === user.tenantId);
    };

    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleDateString();
        }
        return new Date(timestamp).toLocaleDateString();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!consultant) {
        return (
            <Alert severity="error">
                Consultant not found or you don't have permission to view it.
            </Alert>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/consultants')}
                    >
                        Back to Consultants
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                            {getInitials(consultant.firstName, consultant.lastName)}
                        </Avatar>
                        <Box>
                            <Typography variant="h4">
                                {consultant.firstName} {consultant.lastName}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {consultant.jobTitle && `${consultant.jobTitle} • `}{consultant.company}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {canEditConsultant() && (
                    <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => navigate(`/consultants/${id}/edit`)}
                    >
                        Edit Consultant
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Contact Information
                            </Typography>

                            <List>
                                <ListItem sx={{ px: 0 }}>
                                    <Email sx={{ mr: 2, color: 'text.secondary' }} />
                                    <ListItemText
                                        primary="Email"
                                        secondary={consultant.email}
                                    />
                                </ListItem>
                                {consultant.phone && (
                                    <ListItem sx={{ px: 0 }}>
                                        <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                                        <ListItemText
                                            primary="Phone"
                                            secondary={consultant.phone}
                                        />
                                    </ListItem>
                                )}
                                <ListItem sx={{ px: 0 }}>
                                    <Business sx={{ mr: 2, color: 'text.secondary' }} />
                                    <ListItemText
                                        primary="Company"
                                        secondary={consultant.company}
                                    />
                                </ListItem>
                                {consultant.jobTitle && (
                                    <ListItem sx={{ px: 0 }}>
                                        <Work sx={{ mr: 2, color: 'text.secondary' }} />
                                        <ListItemText
                                            primary="Job Title"
                                            secondary={consultant.jobTitle}
                                        />
                                    </ListItem>
                                )}
                                <ListItem sx={{ px: 0 }}>
                                    <Public sx={{ mr: 2, color: 'text.secondary' }} />
                                    <ListItemText
                                        primary="Country"
                                        secondary={consultant.country}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Status & Info
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                                {consultant.isRegisteredUser ? (
                                    <Chip label="Registered User" color="success" variant="outlined" />
                                ) : (
                                    <Chip label="Third Party" color="default" variant="outlined" />
                                )}
                                {consultant.isActive ? (
                                    <Chip label="Active" color="primary" variant="outlined" />
                                ) : (
                                    <Chip label="Inactive" color="default" variant="outlined" />
                                )}
                            </Box>

                            <Typography variant="body2" color="text.secondary">
                                Added: {formatDate(consultant.createdAt)}
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Presentations
                            </Typography>

                            {/* @ts-ignore - presentations property checking if exists */}
                            {!consultant.presentations || consultant.presentations.length === 0 ? (
                                <Typography color="text.secondary">
                                    No presentations yet
                                </Typography>
                            ) : (
                                <Typography color="text.secondary">
                                    {/* @ts-ignore */}
                                    {consultant.presentations.length} presentations
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ConsultantDetails;
